import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { z } from 'zod'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Chip from '~/components/Chip'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import { usePermissions } from '~/hooks/usePermissions'
import { GATEWAY_FIELDS, GATEWAY_LABELS, type GatewayProviderType, type GatewayScopeType, type PaymentGatewayType, paymentGatewayService } from '~/services/payment-gateway.service'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'

/**
 * Gestión de pasarelas de pago, reutilizable.
 *
 * Vive en dos sitios y por eso es un componente y no una página: en
 * Configuración (donde root además alterna entre la empresa activa y el ámbito
 * de sistema) y en la ficha de una empresa concreta, donde `companyId` fija de
 * quién son las pasarelas que se administran.
 *
 * Las credenciales entran en claro y vuelven enmascaradas; dejar el campo vacío
 * al editar conserva la guardada.
 */
export interface PaymentGatewaysManagerPropsType {
  /** Empresa a administrar. Sin él, la empresa activa de la sesión. */
  companyId?: string
  /** Ofrecer el conmutador empresa/sistema (sólo tiene sentido en Configuración). */
  allowSystemScope?: boolean
}

const PaymentGatewaysManager: Component<PaymentGatewaysManagerPropsType> = props => {
  const toast = useToast()
  const alert = useAlert()
  const { can, isRoot } = usePermissions()

  const [scope, setScope] = createSignal<GatewayScopeType>('company')
  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<PaymentGatewayType | null>(null)
  const [busyId, setBusyId] = createSignal<number | null>(null)

  /** El ámbito de sistema no es de ninguna empresa, así que no lleva companyId. */
  const target = () => (scope() === 'system' ? undefined : props.companyId)

  const [gateways, { refetch }] = createResource(
    () => ({ scope: scope(), companyId: props.companyId }),
    async ({ scope: s, companyId }) => {
      try {
        return await paymentGatewayService.list(s, s === 'system' ? undefined : companyId)
      } catch {
        return []
      }
    }
  )

  // Qué pasarelas tienen driver de verdad hoy. Las demás se pueden guardar pero
  // no cobran, y la tarjeta lo dice en vez de fallar al primer pago.
  const [supported] = createResource(async () => {
    try {
      return await paymentGatewayService.providers()
    } catch {
      return [] as GatewayProviderType[]
    }
  })

  const canEdit = () => can(P.paymentGateways.update)

  const gatewaySchema = z
    .object({
      provider: z.enum(['mercadopago', 'wompi', 'stripe', 'sandbox_local']),
      label: z.string().trim().min(1, 'El nombre es obligatorio'),
      mode: z.enum(['sandbox', 'live']).default('sandbox'),
      publicKey: z.string().trim().default(''),
      accessToken: z.string().default(''),
      privateKey: z.string().default(''),
      secretKey: z.string().default(''),
      eventsSecret: z.string().default(''),
      integritySecret: z.string().default(''),
      webhookSecret: z.string().default(''),
      successUrl: z.string().trim().default(''),
      failureUrl: z.string().trim().default(''),
      isDefault: z.boolean().default(false)
    })
    .superRefine((data, ctx) => {
      // Wompi manda la llave pública en la URL del checkout: sin ella no hay cobro.
      if (data.provider === 'wompi' && !data.publicKey && !editing()?.publicKey) {
        ctx.addIssue({ code: 'custom', path: ['publicKey'], message: 'Wompi necesita la llave pública' })
      }

      /**
       * En Wompi el prefijo de la llave DECLARA el ambiente, y tiene que casar
       * con el modo elegido: la API usa una URL distinta para cada uno. Avisar
       * aquí ahorra el error genérico que devuelve Wompi cuando no coinciden.
       */
      if (data.provider === 'wompi') {
        const esperado = data.mode === 'sandbox' ? 'pruebas' : 'producción'
        const ambienteDe = (key: string): 'pruebas' | 'producción' | null => {
          if (!key) return null
          if (key.startsWith('test_') || key.includes('_test_')) return 'pruebas'
          if (key.startsWith('prod_') || key.includes('_prod_')) return 'producción'
          return null
        }
        for (const campo of ['publicKey', 'privateKey', 'integritySecret', 'eventsSecret'] as const) {
          const valor = String(data[campo] ?? '')
          const ambiente = ambienteDe(valor)
          if (ambiente && ambiente !== esperado) {
            ctx.addIssue({
              code: 'custom',
              path: [campo],
              message: `Esta llave es de ${ambiente} y la pasarela está en modo ${esperado}`
            })
          }
        }
      }

      // Al crear hay que dar las credenciales; al editar se pueden dejar vacías
      // porque el backend conserva las que ya tenía.
      if (editing()) return
      const requerido: Record<GatewayProviderType, string[]> = {
        mercadopago: ['accessToken'],
        wompi: ['privateKey', 'integritySecret'],
        stripe: ['secretKey'],
        sandbox_local: []
      }
      for (const key of requerido[data.provider] ?? []) {
        if (!String(data[key as keyof typeof data] ?? '').trim()) {
          ctx.addIssue({
            code: 'custom',
            path: [key],
            message: `${GATEWAY_LABELS[data.provider]} necesita este dato para poder cobrar`
          })
        }
      }
    })

  const form = useForm({
    schema: gatewaySchema,
    initialValues: () => {
      const g = editing()
      const settings = (g?.settings ?? {}) as Record<string, string>
      return {
        provider: g?.provider ?? 'mercadopago',
        label: g?.label ?? '',
        mode: g?.mode ?? 'sandbox',
        publicKey: g?.publicKey ?? '',
        // Los secretos nunca se precargan: sólo se escriben si se cambian.
        accessToken: '',
        privateKey: '',
        secretKey: '',
        eventsSecret: '',
        integritySecret: '',
        webhookSecret: '',
        successUrl: settings.successUrl ?? '',
        failureUrl: settings.failureUrl ?? '',
        isDefault: g?.isDefault ?? false
      }
    },
    onSubmit: async values => {
      const provider = values.provider as GatewayProviderType

      // Sólo viajan las credenciales que el usuario escribió: un campo vacío
      // significa "deja la que está", no "bórrala".
      const credentials: Record<string, string> = {}
      for (const field of GATEWAY_FIELDS[provider]) {
        const value = String(values[field.key as keyof typeof values] ?? '').trim()
        if (value) credentials[field.key] = value
      }

      const payload = {
        provider,
        label: values.label as string,
        mode: values.mode as 'sandbox' | 'live',
        publicKey: (values.publicKey as string) || null,
        ...(Object.keys(credentials).length ? { credentials } : {}),
        settings: {
          ...(values.successUrl ? { successUrl: values.successUrl as string } : {}),
          ...(values.failureUrl ? { failureUrl: values.failureUrl as string } : {})
        },
        isDefault: Boolean(values.isDefault),
        scope: scope(),
        ...(target() ? { companyId: target() } : {})
      }

      if (editing()) {
        await paymentGatewayService.update(editing()!.id, payload)
        toast.addToast({ title: 'Pasarela actualizada', type: 'success' })
      } else {
        await paymentGatewayService.create(payload)
        toast.addToast({ title: 'Pasarela creada', type: 'success' })
      }
      setShowModal(false)
      await refetch()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  /** El proveedor elegido en el formulario, para saber qué campos pintar. */
  const selectedProvider = createMemo(() => (form.values().provider as GatewayProviderType) ?? 'mercadopago')

  const openCreate = () => {
    setEditing(null)
    form.reset()
    setShowModal(true)
  }

  const openEdit = (g: PaymentGatewayType) => {
    setEditing(g)
    setShowModal(true)
  }

  const makeDefault = async (g: PaymentGatewayType) => {
    setBusyId(g.id)
    try {
      await paymentGatewayService.setDefault(g.id, scope(), target())
      toast.addToast({ title: `"${g.label}" es ahora la pasarela por defecto`, type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setBusyId(null)
    }
  }

  const testConnection = async (g: PaymentGatewayType) => {
    setBusyId(g.id)
    try {
      const message = await paymentGatewayService.test(g.id, scope(), target())
      toast.addToast({ title: 'Conexión correcta', description: message, type: 'success' })
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (g: PaymentGatewayType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar pasarela',
      message: `¿Eliminar "${g.label}"? Los pagos ya hechos con ella conservan su histórico.`
    })
    if (!ok) return
    try {
      await paymentGatewayService.delete(g.id, scope(), target())
      toast.addToast({ title: 'Pasarela eliminada', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const providerOptions = createMemo(() =>
    (Object.keys(GATEWAY_LABELS) as GatewayProviderType[]).map(value => ({
      value,
      title: GATEWAY_LABELS[value],
      subtitle: (supported() ?? []).includes(value) ? undefined : 'Todavía sin driver: no puede cobrar'
    }))
  )

  return (
    <>
      <div class="flex flex-wrap items-center justify-end gap-2">
        {/* El ámbito de sistema afecta a todas las empresas, así que sólo root. */}
        <Show when={props.allowSystemScope && isRoot()}>
          <ButtonGroup size="sm" variant="soft">
            <Button color={scope() === 'company' ? 'primary' : 'default'} onClick={() => setScope('company')}>
              Esta empresa
            </Button>
            <Button color={scope() === 'system' ? 'primary' : 'default'} onClick={() => setScope('system')}>
              Sistema
            </Button>
          </ButtonGroup>
        </Show>
        <Show when={canEdit()}>
          <Button prependIcon="material-symbols-light:add-circle-outline" onClick={openCreate}>
            Añadir pasarela
          </Button>
        </Show>
      </div>

      <Show when={scope() === 'system'}>
        <Alert variant="info" class="mt-4">
          Estás viendo la configuración del <strong>sistema</strong>: la usan las empresas que no tienen pasarela propia.
        </Alert>
      </Show>

      <Card class="mt-4">
        <Card.Body>
          <Show when={gateways.error}>
            <Alert variant="danger" class="mb-4">
              No se pudo cargar: {resolveError(gateways.error)}
            </Alert>
          </Show>

          <Show
            when={gateways()?.length}
            fallback={
              <div class="flex flex-col items-center justify-center gap-3 py-10 text-center">
                <Icon icon="material-symbols-light:credit-card-outline" size={40} class="text-foreground-subtle" />
                <p class="text-foreground-subtle max-w-md">
                  Aún no hay ninguna pasarela configurada. Añade una para poder cobrar en línea; en pruebas puedes usar la pasarela simulada, que no necesita internet.
                </p>
                <Show when={canEdit()}>
                  <Button prependIcon="material-symbols-light:add-circle-outline" onClick={openCreate}>
                    Añadir pasarela
                  </Button>
                </Show>
              </div>
            }
          >
            <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <For each={gateways()}>
                {g => (
                  <div class="rounded-lg border border-border p-3 flex flex-col gap-3">
                    <div class="flex items-start justify-between gap-2">
                      <div class="min-w-0">
                        <p class="font-medium text-foreground truncate">{g.label}</p>
                        <p class="text-xs text-foreground-subtle">{GATEWAY_LABELS[g.provider]}</p>
                      </div>
                      <Show when={g.isDefault}>
                        <Chip size="sm" variant="soft" color="success">
                          Por defecto
                        </Chip>
                      </Show>
                    </div>

                    <div class="flex flex-wrap gap-1.5">
                      <Chip size="xs" variant="soft" color={g.mode === 'live' ? 'danger' : 'info'}>
                        {g.mode === 'live' ? 'Producción' : 'Pruebas'}
                      </Chip>
                      <Show when={!g.supported}>
                        <Chip size="xs" variant="soft" color="warning">
                          Sin driver
                        </Chip>
                      </Show>
                      <Show when={!g.isActive}>
                        <Chip size="xs" variant="soft" color="default">
                          Inactiva
                        </Chip>
                      </Show>
                    </div>

                    <Show when={Object.keys(g.credentials).length}>
                      <div class="text-xs text-foreground-subtle space-y-0.5">
                        <For each={Object.entries(g.credentials)}>
                          {([key, value]) => (
                            <div class="flex justify-between gap-2">
                              <span class="truncate">{key}</span>
                              <span class="font-mono truncate">{value}</span>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>

                    <div class="flex flex-wrap gap-2 mt-auto pt-1">
                      <Show when={canEdit()}>
                        <Button size="sm" variant="outlined" color="default" disabled={busyId() === g.id} onClick={() => testConnection(g)} prependIcon="material-symbols-light:wifi-tethering">
                          Probar
                        </Button>
                        <Show when={!g.isDefault}>
                          <Button size="sm" variant="soft" color="info" disabled={busyId() === g.id} onClick={() => makeDefault(g)}>
                            Usar por defecto
                          </Button>
                        </Show>
                        <ButtonGroup size="sm" variant="soft">
                          <Button color="warning" onClick={() => openEdit(g)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
                          <Button color="danger" onClick={() => remove(g)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
                        </ButtonGroup>
                      </Show>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </Card.Body>
      </Card>

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar pasarela' : 'Nueva pasarela'}</h2>
        </Modal.Header>

        <Modal.Body>
          <Form id="gateway-form" controller={form}>
            <div class="space-y-4">
              <Select
                name="provider"
                label="Pasarela"
                value={selectedProvider()}
                options={providerOptions()}
                // Al editar no se cambia de proveedor: las credenciales guardadas
                // son de otro sitio y el histórico de pagos apunta a éste.
                disabled={Boolean(editing())}
              />

              <Input name="label" label="Nombre" placeholder="p.ej. Wompi producción" />

              <Select
                name="mode"
                label="Modo"
                value={(form.values().mode as string) ?? 'sandbox'}
                options={[
                  { value: 'sandbox', title: 'Pruebas (sandbox)' },
                  { value: 'live', title: 'Producción — cobra dinero real' }
                ]}
              />

              <Show when={selectedProvider() === 'wompi'}>
                <Alert variant="info">
                  En Wompi el ambiente lo declara el prefijo de cada llave (<code>test_</code> o <code>prod_</code>) y tiene que coincidir con el modo elegido arriba.
                </Alert>
              </Show>

              <For each={GATEWAY_FIELDS[selectedProvider()]}>
                {field => (
                  <div>
                    <Input
                      name={field.key}
                      label={field.label}
                      type="password"
                      placeholder={editing()?.credentials?.[field.key] ? `Guardada (${editing()!.credentials[field.key]}) — deja vacío para conservarla` : ''}
                    />
                    <Show when={field.help}>
                      <p class="text-xs text-foreground-subtle mt-1">{field.help}</p>
                    </Show>
                  </div>
                )}
              </For>

              <Input
                name="publicKey"
                label={selectedProvider() === 'wompi' ? 'Llave pública' : 'Llave pública (opcional)'}
                placeholder={selectedProvider() === 'wompi' ? 'pub_test_... / pub_prod_...' : 'No es secreta: la usa el navegador'}
              />

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input name="successUrl" label="URL de retorno (éxito)" placeholder="Por defecto, la página del pedido" />
                <Input name="failureUrl" label="URL de retorno (fallo)" placeholder="Por defecto, la página del pedido" />
              </div>

              <Checkbox name="isDefault" label="Cobrar con esta pasarela" checked={Boolean(form.values().isDefault)} />
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="text" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            {/* El form vive en el Body: sin `form` el botón no envía nada. */}
            <Button type="submit" form="gateway-form" disabled={form.submitting()}>
              {form.submitting() ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default PaymentGatewaysManager
